import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useAuthStatus from "../../hooks/useAuthStatus";
import { getTransactionHistory } from "../../api/apiServices";
import { TxIconCell } from "../../utils/txIconHelper";
import { useAssets } from "../../hooks/useAssets";
import { useWallet } from "../../hooks/useWallet";
import { useServices } from "../../hooks/useServices";
import MobileBottomNav from "../../components/feature/MobileBottomNav";

const Skeleton = ({ className = "" }) => <div className={`animate-pulse bg-skeleton rounded-xl ${className}`} />;

function PortfolioCardSkeleton() {
  return (
    <div className="mx-5 mt-4 rounded-2xl p-5 mb-5 overflow-hidden" style={{ background: "var(--gradient-hero)" }}>
      <div className="h-3 w-36 rounded-full bg-white/20 animate-pulse mb-3" />
      <div className="h-9 w-48 rounded-xl bg-white/20 animate-pulse mb-2" />
      <div className="h-3 w-40 rounded-full bg-white/15 animate-pulse mb-5" />
      <div className="grid grid-cols-2 gap-3">
        <div className="h-10 rounded-xl bg-white/20 animate-pulse" />
        <div className="h-10 rounded-xl bg-white/10 animate-pulse" />
      </div>
    </div>
  );
}
function AssetCardSkeleton() {
  return (
    <div className="bg-surface rounded-2xl px-4 py-4 border border-border shadow-card space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="w-11 h-11 rounded-full" />
          <div className="space-y-1.5"><Skeleton className="h-3 w-12" /><Skeleton className="h-2.5 w-20" /></div>
        </div>
        <div className="items-end flex flex-col gap-1.5"><Skeleton className="h-3 w-24" /><Skeleton className="h-2.5 w-16" /></div>
      </div>
      <Skeleton className="h-1.5 w-full rounded-full" />
      <div className="flex gap-2"><Skeleton className="flex-1 h-9 rounded-xl" /><Skeleton className="flex-1 h-9 rounded-xl" /><Skeleton className="flex-1 h-9 rounded-xl" /></div>
    </div>
  );
}

const formatNaira = (n) => `₦${Number(n || 0).toLocaleString("en-NG", { maximumFractionDigits: 2 })}`;

function TxSkeleton() {
  return (
    <div className="space-y-2">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-surface rounded-2xl px-4 py-3 flex items-center justify-between border border-border shadow-card">
          <div className="flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div className="space-y-1.5"><Skeleton className="h-3 w-24" /><Skeleton className="h-2.5 w-16" /></div>
          </div>
          <div className="items-end flex flex-col gap-1.5"><Skeleton className="h-3 w-20" /><Skeleton className="h-2.5 w-12" /></div>
        </div>
      ))}
    </div>
  );
}

export default function Wallet() {
  const navigate        = useNavigate();
  const isAuthenticated = useAuthStatus();

  // ── Service flags ─────────────────────────────────────────────
  const { isEnabled } = useServices();

  const { priceMap, loading: ratesLoading } = useAssets({ pollInterval: 60_000 });
  const { nairaBalance, holdings, totalPortfolio, loading: walletLoading } =
    useWallet({ enabled: isAuthenticated, priceMap, pollInterval: 30_000 });

  const portfolioLoading = isAuthenticated && walletLoading;

  // ── Transaction history ─────────────────────────────────────
  const [transactions,  setTransactions]  = useState([]);
  const [txLoading,     setTxLoading]     = useState(true);
  const txInitialLoad                     = useRef(false);

  useEffect(() => {
    if (!isAuthenticated) { setTxLoading(false); return; }
    if (!txInitialLoad.current) setTxLoading(true);
    getTransactionHistory()
      .then(r => { if (r.status) setTransactions(r.data || []); })
      .catch(console.error)
      .finally(() => { setTxLoading(false); txInitialLoad.current = true; });
  }, [isAuthenticated]);

  const recentTxs = useMemo(() =>
    [...transactions].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 10),
    [transactions]
  );

  const handleProtectedAction = (path) => {
    if (!isAuthenticated) navigate("/auth");
    else navigate(path);
  };

  // Which portfolio card buttons to show
  const depositEnabled          = isEnabled("deposits");
  const withdrawCryptoEnabled   = isEnabled("withdrawals");
  const nairaWithdrawalEnabled  = isEnabled("naira_withdrawal");
  const fiatDepositEnabled      = isEnabled("fiat_deposit");
  const tradingEnabled          = isEnabled("trading");

  return (
    <div className="min-h-screen bg-bg pb-28">
      <div className="bg-surface px-5 pt-12 pb-5 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-primary font-bold text-xl font-display">My Wallet</h1>
            <p className="text-muted text-sm">All your asset holdings</p>
          </div>
          {!isAuthenticated && (
            <button onClick={() => navigate("/auth")}
              className="px-5 py-2 text-sm font-semibold text-brand-text border border-brand-border rounded-xl hover:bg-brand-light transition-colors">
              Sign In
            </button>
          )}
        </div>
      </div>

      {/* Portfolio Card */}
      {portfolioLoading ? <PortfolioCardSkeleton /> : (
        <div className="mx-5 mt-4 rounded-2xl p-5 relative overflow-hidden mb-5"
          style={{ background: "var(--gradient-hero)", boxShadow: "var(--card-top-highlight)" }}>

          {/* Sheen — same token-driven highlight as Home/Profile hero surfaces */}
          <div className="absolute inset-0 pointer-events-none" style={{ background: "var(--gradient-hero-sheen)" }} />

          {!isAuthenticated && (
            <div className="absolute inset-0 backdrop-blur-md rounded-2xl flex items-center justify-center z-20" style={{ background: "var(--color-overlay)" }}>
              <div className="text-center px-6">
                <i className="ri-lock-2-line text-white text-5xl mb-4" />
                <p className="text-white font-semibold text-lg mb-1">Sign in to view portfolio</p>
                <p className="text-white/60 text-sm mb-6">Your assets are private and secure</p>
                <button onClick={() => navigate("/auth")} className="px-8 py-3 bg-white text-brand-text rounded-2xl font-semibold hover:bg-white/90 transition-colors">Login to Unlock</button>
              </div>
            </div>
          )}

          <div className="relative z-10">
            <p className="text-white/60 text-xs font-medium mb-1">Total Portfolio Value</p>
            <p className="text-white text-3xl font-bold font-display">
              {isAuthenticated
                ? formatNaira(isEnabled("trading") ? totalPortfolio : nairaBalance)
                : "₦ ••••••"}
            </p>
            <p className="text-white/50 text-xs mt-1 mb-4">
              {isEnabled("trading")
                ? <>Naira Balance: <span className="text-white font-semibold">{isAuthenticated ? formatNaira(nairaBalance) : "₦ ••••"}</span></>
                : <span className="text-white/40">Crypto trading is currently unavailable</span>}
            </p>

            {/* Only render the button row if at least one action is available */}
            {(() => {
              // Build the visible action buttons dynamically
              const btns = [
                depositEnabled         && { label: "Deposit",  icon: "ri-arrow-down-circle-line", path: "/deposit",        style: "bg-white/15 hover:bg-white/25" },
                nairaWithdrawalEnabled && { label: "Withdraw", icon: "ri-bank-line",               path: "/withdraw-naira",       style: "bg-white/8 border border-white/15 hover:bg-white/15" },
                fiatDepositEnabled && { label: "Fund",     icon: "ri-bank-card-line",          path: "/deposit-fiat",   style: "bg-white/15 hover:bg-white/25" },
              ].filter(Boolean);

              if (btns.length === 0) return null;
              return (
                <div className={`grid gap-2`} style={{ gridTemplateColumns: `repeat(${Math.min(btns.length, 3)}, 1fr)` }}>
                  {btns.map(btn => (
                    <button key={btn.label} onClick={() => handleProtectedAction(btn.path)}
                      className={`flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-medium transition-colors ${btn.style}`}>
                      <i className={btn.icon} /> {btn.label}
                    </button>
                  ))}
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Assets — hidden when trading is off */}
      {isEnabled("trading") && <div className="px-5 relative">
        <h2 className="text-primary font-bold text-sm mb-3 font-display">Assets</h2>

        {!isAuthenticated && (
          <div className="absolute inset-0 backdrop-blur-md z-20 flex items-center justify-center rounded-3xl border border-border" style={{ background: "var(--color-surface)", opacity: 0.95 }}>
            <div className="text-center px-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-surface-muted flex items-center justify-center">
                <i className="ri-lock-2-line text-muted text-3xl" />
              </div>
              <p className="font-semibold text-secondary text-lg mb-4">Login to see your assets</p>
              <button onClick={() => navigate("/auth")}
                className="px-8 py-3 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-2xl text-sm flex items-center gap-2 mx-auto transition-colors">
                <i className="ri-login-box-line" /> Sign in to Unlock
              </button>
            </div>
          </div>
        )}

        <div className={`space-y-3 ${!isAuthenticated ? "blur-sm pointer-events-none" : ""}`}>
          {portfolioLoading ? (
            [...Array(3)].map((_, i) => <AssetCardSkeleton key={i} />)
          ) : holdings.length > 0 ? (
            holdings.map(h => {
              const pct = totalPortfolio > 0 ? ((h.valueNGN / totalPortfolio) * 100).toFixed(1) : 0;
              // Asset colors are per-asset runtime data (Bitcoin orange, Ethereum
              // blue, etc.), not one of the fixed semantic tones, so they stay as
              // computed inline values rather than token references — there's no
              // finite "asset-light" token since the palette here is dynamic.
              const assetActions = [
                isEnabled("trading")     && { label: "Buy",  path: "/buy",             color: h.color, bg: `${h.color}15`, textCls: "" },
                isEnabled("trading")     && { label: "Sell", path: "/sell",             color: null,    bg: null,           textCls: "bg-surface-hover text-secondary hover:bg-surface-muted" },
                isEnabled("withdrawals") && { label: "Send", path: "/withdraw-crypto",  color: null,    bg: null,           textCls: "bg-surface-hover text-secondary hover:bg-surface-muted" },
              ].filter(Boolean);

              return (
                <div key={h.symbol} className="bg-surface rounded-2xl px-4 py-4 border border-border shadow-card">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center overflow-hidden" style={{ background: `${h.color}18`, boxShadow: `inset 0 0 0 1px ${h.color}30` }}>
                        {h.image
                          ? <img src={h.image} alt={h.symbol} className="w-7 h-7 object-contain" />
                          : <i className={`${h.icon} text-xl`} style={{ color: h.color }} />}
                      </div>
                      <div>
                        <p className="text-primary text-sm font-bold">{h.symbol}</p>
                        <p className="text-muted text-xs">{h.name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-primary text-sm font-bold">{formatNaira(h.valueNGN)}</p>
                      <p className="text-muted text-xs">{h.totalBalance} {h.symbol}</p>
                    </div>
                  </div>

                  {h.networks.length > 1 && (
                    <div className="mb-3 space-y-1">
                      {h.networks.map(net => (
                        <div key={net.network} className="flex items-center justify-between px-2 py-1.5 bg-surface-muted rounded-lg">
                          <span className="text-[10px] font-bold text-muted uppercase">{net.network}</span>
                          <span className="text-[10px] font-semibold text-secondary">{net.balance} {h.symbol}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-2">
                    <span className="text-muted text-xs">{pct}% of portfolio</span>
                    <span className={`text-xs font-semibold ${h.change24h >= 0 ? "text-success" : "text-danger"}`}>
                      {h.change24h >= 0 ? "+" : ""}{h.change24h.toFixed(2)}% 24h
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-surface-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: h.color }} />
                  </div>

                  {/* Asset action buttons — only rendered if service is on */}
                  {assetActions.length > 0 && (
                    <div className="flex gap-2 mt-4">
                      {assetActions.map(btn => (
                        <button key={btn.label} onClick={() => handleProtectedAction(btn.path)}
                          className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-colors ${btn.textCls}`}
                          style={btn.bg ? { background: btn.bg, color: btn.color } : {}}>
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div className="bg-surface rounded-2xl p-10 text-center border border-border shadow-card">
              <div className="w-16 h-16 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-wallet-3-line text-muted text-2xl" />
              </div>
              <p className="text-secondary font-semibold mb-1">No assets yet</p>
              <p className="text-muted text-sm mb-4">Deposit or buy crypto to get started</p>
              {depositEnabled && (
                <button onClick={() => handleProtectedAction("/deposit")}
                  className="px-6 py-2.5 bg-brand hover:bg-brand-hover text-on-brand font-semibold rounded-xl text-sm transition-colors">
                  Deposit Now
                </button>
              )}
            </div>
          )}
        </div>
      </div>}

      {/* ── Transaction History ── */}
      <div className="px-5 mt-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-primary font-bold text-sm font-display">
            Transaction History
          </h2>
          <button onClick={() => handleProtectedAction("/transactions")} className="text-brand text-xs font-medium">
            See all
          </button>
        </div>

        {!isAuthenticated && (
          <div className="bg-surface rounded-2xl border border-border shadow-card p-8 text-center">
            <i className="ri-lock-line text-faint text-3xl mb-2 block" />
            <p className="text-muted text-sm">Sign in to view your transactions</p>
          </div>
        )}

        {isAuthenticated && (
          txLoading ? <TxSkeleton /> : recentTxs.length > 0 ? (
            <div className="space-y-2">
              {recentTxs.map(tx => {
                const label = tx.type === "bill_payment"
                  ? (tx.assetName || tx.asset || "Bill Payment")
                  : `${{ buy:"Bought", sell:"Sold", deposit:"Deposited", withdraw_crypto:"Withdrew", withdrawal:"Withdrew", withdraw_naira:"Withdrew NGN" }[tx.type] || tx.type}${tx.asset && tx.asset !== "NGN" ? " " + tx.asset : ""}`;
                const isMinus = ["sell","withdraw_naira","withdrawal","withdraw_crypto"].includes(tx.type);
                return (
                  <div key={tx._id} onClick={() => handleProtectedAction(`/transaction/${tx._id}`)}
                    className="bg-surface rounded-2xl px-4 py-3 flex items-center justify-between border border-border shadow-card cursor-pointer hover:border-brand-border active:scale-[0.99] transition-all">
                    <div className="flex items-center gap-3">
                      <TxIconCell tx={tx} size="w-10 h-10" rounded="rounded-xl" />
                      <div>
                        <p className="text-primary text-sm font-semibold">{label}</p>
                        <p className="text-muted text-xs">
                          {new Date(tx.createdAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                          {" · "}
                          <span className={["completed","delivered"].includes(tx.status) ? "text-success" : ["pending","processing"].includes(tx.status) ? "text-warning" : "text-muted"}>
                            {tx.status}
                          </span>
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-primary text-sm font-bold">{isMinus ? "−" : ""}₦{(tx.nairaAmount || 0).toLocaleString()}</p>
                      {tx.cryptoAmount > 0 && (
                        <p className="text-muted text-xs">{isMinus ? "−" : "+"}{tx.cryptoAmount} {tx.asset}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-surface rounded-2xl border border-border shadow-card p-8 text-center">
              <div className="w-12 h-12 bg-surface-muted rounded-2xl flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-list-3-line text-muted text-xl" />
              </div>
              <p className="text-secondary font-semibold text-sm mb-1">No transactions yet</p>
              <p className="text-muted text-xs">Your transaction history will appear here</p>
            </div>
          )
        )}
      </div>

      <MobileBottomNav />
    </div>
  );
}
